import { useState, useEffect } from 'react';
import { Search, Loader2, AlertCircle, X, Tag, Filter } from 'lucide-react';
import axios from 'axios';
import { DocumentExportActions } from './DocumentExportActions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const StockAvailability = () => {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Filter state
  const [filterPriceMin, setFilterPriceMin] = useState('');
  const [filterPriceMax, setFilterPriceMax] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({ priceMin: '', priceMax: '' });

  // Export options
  const [includeStock, setIncludeStock] = useState(true);
  const [includePrice, setIncludePrice] = useState(true);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => { fetchInventory(); }, []);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/inventory/all-products-with-price`, getAuthHeaders());
      setInventory(res.data || []);
    } catch (err) { setError('Failed to fetch inventory'); }
    finally { setLoading(false); }
  };

  const applyFilters = () => setAppliedFilters({ priceMin: filterPriceMin, priceMax: filterPriceMax });
  const clearFilters = () => { setFilterPriceMin(''); setFilterPriceMax(''); setAppliedFilters({ priceMin: '', priceMax: '' }); };
  const hasActiveFilters = appliedFilters.priceMin !== '' || appliedFilters.priceMax !== '';

  const filtered = (() => {
    const tokens = searchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
    let result = inventory.filter(item => {
      if (!tokens.length) return true;
      const fields = [item.product_name, item.product_code, item.brand_name, item.category_name]
        .map(s => (s || '').toLowerCase()).join(' ');
      return tokens.every(tok => fields.includes(tok));
    });
    if (appliedFilters.priceMin !== '') {
      const min = parseFloat(appliedFilters.priceMin);
      if (!isNaN(min)) result = result.filter(item => (item.display_rate || 0) >= min);
    }
    if (appliedFilters.priceMax !== '') {
      const max = parseFloat(appliedFilters.priceMax);
      if (!isNaN(max)) result = result.filter(item => (item.display_rate || 0) <= max);
    }
    return result;
  })();
  const stockColumns = [
    { label: '#', value: (_, index) => index + 1, type: 'number' },
    { label: 'Product Name', value: item => item.product_name || '-' },
  ];
  if (includeStock) stockColumns.push({ label: 'In Stock', value: item => Number(item.quantity || 0), type: 'number' });
  if (includePrice) stockColumns.push({ label: 'Display Price', value: item => Number(item.display_rate || 0), type: 'number' });
  const exportConfig = {
    title: 'Stock Availability',
    metadata: [{ label: 'Products', value: filtered.length }, { label: 'Generated', value: new Date().toLocaleDateString('en-IN') }],
    columns: stockColumns,
    rows: filtered,
    shareLines: [`Products: ${filtered.length}`],
  };

  return (
    <div data-testid="stock-availability-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Stock Availability</h1>
        <p className="text-slate-600 mt-1">Product listing with display prices</p>
      </div>

      {/* Search */}
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-4 print:hidden">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search by product name, code, brand or category..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            data-testid="search-input"
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6 print:hidden">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Filters</span>
          {hasActiveFilters && (
            <button onClick={clearFilters} data-testid="clear-filters-btn" className="ml-auto text-xs text-red-500 hover:text-red-600 font-medium">Clear All</button>
          )}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Min Display Price</label>
            <input
              type="number" value={filterPriceMin} onChange={e => setFilterPriceMin(e.target.value)}
              placeholder="₹ Min" data-testid="filter-price-min" min="0"
              className="w-36 px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Max Display Price</label>
            <input
              type="number" value={filterPriceMax} onChange={e => setFilterPriceMax(e.target.value)}
              placeholder="₹ Max" data-testid="filter-price-max" min="0"
              className="w-36 px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none"
            />
          </div>
          <button onClick={applyFilters} data-testid="apply-filters-btn"
            className="px-6 py-2 bg-orange-500 text-white text-sm font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 transition-colors">
            Apply
          </button>
          {hasActiveFilters && (
            <button onClick={clearFilters} data-testid="clear-filters-btn-2"
              className="px-6 py-2 border border-slate-200 text-sm text-red-500 hover:text-red-600 hover:border-red-300 rounded-sm transition-colors font-medium">
              Clear
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Results count */}
      {!loading && (
        <p className="text-xs text-slate-500 mb-3 uppercase tracking-wider font-medium">
          {filtered.length} product{filtered.length !== 1 ? 's' : ''} found
        </p>
      )}

      {/* Table */}
      <div className="bg-white rounded-sm border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <Tag className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No products found</h3>
            <p className="text-slate-500">Try adjusting your search or filters</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="px-5 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Product Name</th>
                <th className="px-5 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">In Stock</th>
                <th className="px-5 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Display Price</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => (
                <tr key={item.id} data-testid={`stock-row-${item.id}`}
                  className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      {item.product_image ? (
                        <img src={item.product_image} alt="" className="h-8 w-8 rounded object-cover border border-slate-200 flex-shrink-0" />
                      ) : (
                        <div className="h-8 w-8 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
                          <Tag className="h-4 w-4 text-slate-300" />
                        </div>
                      )}
                      <span className="font-medium text-slate-900">{item.product_name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${(item.quantity ?? 0) > 0 ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                      {item.quantity ?? 0}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right font-bold text-orange-600">
                    {item.display_rate != null ? `₹${item.display_rate.toLocaleString()}` : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Export options + Share buttons */}
      {!loading && filtered.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center justify-end gap-3 print:hidden">
          {/* Checkboxes */}
          <div className="flex items-center gap-5 mr-auto">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" checked={includeStock} onChange={e => setIncludeStock(e.target.checked)}
                data-testid="include-stock-checkbox"
                className="w-4 h-4 accent-orange-500 cursor-pointer" />
              <span className="text-sm font-medium text-slate-700">Stock Data</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" checked={includePrice} onChange={e => setIncludePrice(e.target.checked)}
                data-testid="include-price-checkbox"
                className="w-4 h-4 accent-orange-500 cursor-pointer" />
              <span className="text-sm font-medium text-slate-700">Price Data</span>
            </label>
          </div>
          <DocumentExportActions config={exportConfig} testIdPrefix="stock-availability" />
        </div>
      )}
    </div>
  );
};
